import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import FoundingBadge from "@/components/FoundingBadge";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";
import useWishlistCount from "@/hooks/useWishlistCount";

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const wishlistCount = useWishlistCount();

  const isFarmUser = ["vendor","farm_admin","farm_staff"].includes(user?.role);
  const isAdminUser = ["admin","super_admin"].includes(user?.role);
  const isAmbassador = user?.role === "ambassador";
  const dashHref =
    isAdminUser ? "/admin"
    : isFarmUser ? "/vendor"
    : isAmbassador ? "/ambassador/dashboard"
    : "/account";
  const dashLabel =
    isAdminUser ? "Admin"
    : isFarmUser ? "Farm"
    : isAmbassador ? "Ambassador"
    : "Account";

  const links = [
    { to: "/marketplace", label: "Marketplace" },
    { to: "/drops", label: "Drops" },
    { to: "/ranchers", label: "Ranchers" },
    { to: "/how-it-works", label: "How it Works" },
    { to: "/recipes", label: "Recipes" },
    { to: "/pricing", label: "Membership" },
    { to: "/about", label: "About Us" },
  ];

  return (
    <nav className="nav-glass z-50" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <span className="font-display text-2xl tracking-tight" style={{color: "var(--ff-primary)"}}>
            The Farm To You
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map(l => (
            <Link key={l.label} to={l.to}
              className={`text-sm font-medium transition-colors ${loc.pathname.startsWith(l.to) ? 'text-[var(--ff-primary)]' : 'text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]'}`}
              data-testid={`nav-link-${l.to.replace('/','')}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart" className="p-2 hover:text-[var(--ff-primary)]">
            <ShoppingBag size={20} />
          </Link>
          {user ? (
            <>
              {user.is_founding_member && <FoundingBadge size="xs" data-testid="nav-founding-badge"/>}
              <Link to={dashHref} className="btn-secondary relative" data-testid="nav-dashboard">
                <LayoutDashboard size={16} /> {dashLabel}
                {wishlistCount > 0 && dashLabel === "Account" && (
                  <span
                    className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--ff-citrus)] text-white text-[10px] font-semibold tabular-nums leading-none"
                    data-testid="nav-wishlist-badge"
                    aria-label={`${wishlistCount} saved drops`}
                  >
                    {wishlistCount > 9 ? "9+" : wishlistCount}
                  </span>
                )}
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }}
                className="p-2 text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]"
                data-testid="nav-logout">
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-secondary !py-2 !px-4 text-sm" data-testid="nav-login"><User size={14}/> Sign in</Link>
              <Link to="/pricing" className="btn-primary !py-2 !px-4 text-sm" data-testid="nav-join">Become a member</Link>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--ff-border)] bg-white/95 px-6 py-4 space-y-3">
          {links.map(l => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="block py-2" data-testid={`mnav-${l.to.replace('/','')}`}>
              {l.label}
            </Link>
          ))}
          <Link to="/cart" onClick={() => setOpen(false)} className="block py-2">Cart</Link>
          {user ? (
            <>
              <Link to={dashHref} onClick={() => setOpen(false)} className="btn-secondary w-full justify-center">Dashboard</Link>
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="block py-2 text-red-700">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block py-2">Sign in</Link>
              <Link to="/pricing" onClick={() => setOpen(false)} className="btn-primary w-full justify-center">Become a member</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
